import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { downloadPdfGpt } from '../../lib/api';
import { AGE_SEGMENT_SHORT } from './constants';
import { getOptionsArray, getSexChartData, getAgeChartData } from './dataUtils';

function createDateFormatters(i18n, consulta) {
  const localeMap = { ca: 'ca-ES', es: 'es-ES', eu: 'eu-ES', gl: 'gl-ES' };
  const currentLocale = localeMap[i18n.language] || 'ca-ES';
  const entityTz = consulta?.entity_timezone || 'Europe/Madrid';

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const s = String(dateStr).trim();
    const isUTC = s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s);
    if (isUTC) {
      return new Date(s).toLocaleString(currentLocale, {
        timeZone: entityTz, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    }
    const parts = s.split('T');
    if (parts.length < 2) return dateStr;
    const [year, month, day] = parts[0].split('-');
    const [hour, minute] = parts[1].split(':');
    return `${(day||'').padStart(2,'0')}/${(month||'').padStart(2,'0')}/${year} ${(hour||'00').padStart(2,'0')}:${(minute||'00').padStart(2,'0')}`;
  };

  const formatUtcDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    let s = String(dateStr).trim();
    if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
    return new Date(s).toLocaleString(currentLocale, {
      timeZone: entityTz, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return { formatDateTime, formatUtcDateTime };
}

export async function exportToExcel(consulta, results, i18n, t) {
  try {
    const opts = getOptionsArray(consulta);
    const wb = XLSX.utils.book_new();
    const { formatDateTime, formatUtcDateTime } = createDateFormatters(i18n, consulta);

    const sortedOpts = [...opts].sort((a, b) => (results?.results?.[b] || 0) - (results?.results?.[a] || 0));
    const totalCensus = results?.participation?.total_census || 0;

    // Sheet 1: General Results
    const totalOptionsVoted = sortedOpts.reduce((sum, opt) => sum + (results?.results?.[opt] || 0), 0);
    const generalData = [
      [t('excel.resultsTitle') + ': ' + (consulta?.title || '')], [],
      [t('excel.startDate') + ':', formatDateTime(consulta?.start_date)],
      [t('excel.endDate') + ':', formatDateTime(consulta?.end_date)],
      [t('excel.scrutinyDate') + ':', formatUtcDateTime(results?.scrutiny_date)],
      [], [t('excel.census') + ':', totalCensus], [t('excel.totalVotes') + ':', results?.total_votes || 0],
      [], [t('excel.generalResultsSorted')],
      [t('excel.option'), t('excel.votes'), t('excel.percentage')],
      ...sortedOpts.map(opt => {
        const votes = results?.results?.[opt] || 0;
        const pct = totalOptionsVoted > 0 ? ((votes / totalOptionsVoted) * 100).toFixed(2) + '%' : '0%';
        return [opt, votes, pct];
      }),
      [], [t('results.total'), totalOptionsVoted, '100%']
    ];
    const wsGeneral = XLSX.utils.aoa_to_sheet(generalData);
    wsGeneral['!cols'] = [{ wch: 40 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsGeneral, t('excel.sheetGeneral'));

    // Sheet 2: Detail (individual votes grouped by voter)
    const detailed = results?.detailed || [];
    const voterMap = {};
    detailed.forEach(vote => {
      const voterKey = `${vote.date || ''}|${vote.time || ''}|${vote.age_segment || ''}|${vote.sex || ''}|${vote.salt || ''}`;
      if (!voterMap[voterKey]) {
        voterMap[voterKey] = { date: vote.date || '', time: vote.time || '', age_segment: vote.age_segment || t('common.unknown'), sex: vote.sex || t('common.unknown'), options: [] };
      }
      voterMap[voterKey].options.push(vote.option || t('common.unknown'));
    });
    const votersList = Object.values(voterMap);
    const maxOptions = Math.max(1, ...votersList.map(v => v.options.length));
    const optionHeaders = [];
    for (let i = 1; i <= maxOptions; i++) {
      optionHeaders.push(maxOptions === 1 ? t('excel.votedOption') : `${t('excel.votedOption')} ${i}`);
    }
    const detailData = [
      [t('excel.detailTitle')],
      [t('excel.voteDate'), t('excel.voteTime'), t('excel.ageSegment'), t('excel.sex'), ...optionHeaders],
      ...votersList.map(voter => {
        let dateStr = voter.date;
        if (dateStr && dateStr.includes('-')) {
          const parts = dateStr.split('-');
          if (parts.length === 3) dateStr = `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        const optionCells = [];
        for (let i = 0; i < maxOptions; i++) optionCells.push(voter.options[i] || '');
        return [dateStr, voter.time, voter.age_segment, voter.sex, ...optionCells];
      })
    ];
    const wsDetail = XLSX.utils.aoa_to_sheet(detailData);
    const detailCols = [{ wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 12 }];
    for (let i = 0; i < maxOptions; i++) detailCols.push({ wch: 30 });
    wsDetail['!cols'] = detailCols;
    XLSX.utils.book_append_sheet(wb, wsDetail, t('excel.sheetDetail'));

    // Sheet 3: By Age
    if (consulta?.enable_stats_age !== false) {
      const ageChartData = getAgeChartData(results);
      const ageData = [
        [t('excel.resultsByAge')],
        [t('excel.ageSegment'), t('results.total'), ...opts],
        ...ageChartData.map(row => [row.name, row.total, ...opts.map(opt => row[opt] || 0)])
      ];
      const wsAge = XLSX.utils.aoa_to_sheet(ageData);
      wsAge['!cols'] = [{ wch: 25 }, { wch: 10 }, ...opts.map(() => ({ wch: 15 }))];
      XLSX.utils.book_append_sheet(wb, wsAge, t('excel.sheetByAge'));
    }

    // Sheet 4: By Sex
    if (consulta?.enable_stats_sex !== false) {
      const sexChartData = getSexChartData(results);
      const sexData = [
        [t('excel.resultsBySex')],
        [t('excel.sex'), t('results.total'), ...opts],
        ...sexChartData.map(row => [row.name, row.total, ...opts.map(opt => row[opt] || 0)])
      ];
      const wsSex = XLSX.utils.aoa_to_sheet(sexData);
      wsSex['!cols'] = [{ wch: 15 }, { wch: 10 }, ...opts.map(() => ({ wch: 15 }))];
      XLSX.utils.book_append_sheet(wb, wsSex, t('excel.sheetBySex'));
    }

    // Sheet 5: Cross Sex + Age
    if (consulta?.enable_stats_sex !== false && consulta?.enable_stats_age !== false) {
      const crossData = [
        [t('excel.resultsBySexAge')],
        [t('excel.sex'), t('excel.age'), t('results.total'), ...opts],
        ...(results?.by_sex_age || []).map(row => [
          row.sex, AGE_SEGMENT_SHORT[row.age_segment] || row.age_segment, row.total,
          ...opts.map(opt => row.options?.[opt] || 0)
        ])
      ];
      const wsCross = XLSX.utils.aoa_to_sheet(crossData);
      wsCross['!cols'] = [{ wch: 10 }, { wch: 15 }, { wch: 10 }, ...opts.map(() => ({ wch: 15 }))];
      XLSX.utils.book_append_sheet(wb, wsCross, t('excel.sheetSexAge'));
    }

    XLSX.writeFile(wb, `${t('excel.fileResults')}_${consulta?.title?.replace(/\s+/g, "_") || 'consulta'}.xlsx`);
    toast.success(t('excel.exportSuccess'));
  } catch (error) {
    console.error('Export error:', error);
    toast.error(t('excel.exportError'));
  }
}

export async function exportToPdf(id, consulta, t, setExporting) {
  try {
    setExporting(true);
    toast.info(t('results.generatingPdfReport'));
    const response = await downloadPdfGpt(id);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    a.download = filenameMatch ? filenameMatch[1] : `informe_${consulta?.title?.replace(/\s+/g, '_') || 'consulta'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t('pdf.exportSuccess') || 'PDF exportat correctament');
  } catch (error) {
    console.error('PDF export error:', error);
    toast.error(t('pdf.exportError') || 'Error exportant a PDF');
  } finally {
    setExporting(false);
  }
}
